import { useMemo, useState, type FormEvent } from "react";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Link } from "react-router-dom";

import logo from '../assets/img/logo.jpg';
import { ventrisConfig } from '../ventris.config';
import { focusFirstInvalidField } from '../accessibility/focus';

function Onboarding() {
  const [email, setEmail] = useState("");
  const [subscriptionKey, setSubscriptionKey] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mfaMethod, setMfaMethod] = useState<"sms" | "email">("sms");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [onboardingToken, setOnboardingToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"email" | "subscription" | "profile" | "mfa-method" | "mfa" | "password">("email");

  const maskedEmail = useMemo(() => {
    const trimmed = email.trim();
    const domain = trimmed.split("@")[1];
    if (!domain) {
      return "***";
    }
    return `***@${domain}`;
  }, [email]);

  const passwordChecks = useMemo(() => {
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    return {
      hasMinLength,
      hasNumber,
      hasSymbol,
      hasUppercase,
      isValid: hasMinLength && hasNumber && hasSymbol && hasUppercase,
    };
  }, [password]);

  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setError("");
      setValidated(false);
      setStep("subscription");
    }
  };

  const handleSubscriptionSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      setError("");
      try {
        const response = await fetch(ventrisConfig.url + '/api/subscription', {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            subscription_key: subscriptionKey
          })
        });
        const data = await response.json();
        if (data.success) {
          setOnboardingToken(data.onboarding_token || "");
          setValidated(false);
          setStep("profile");
        } else {
          setError("This subscription key is invalid. Please check the email sent by your organization administrator for the correct key and try again.");
        }
      } catch (requestError) {
        console.error(requestError);
      }
    }
  };

  const requestMfa = async (mechanism: "sms" | "email", phone?: string) => {
    const response = await fetch(ventrisConfig.url + '/api/mfa', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        mechanism: mechanism,
        onboarding_token: onboardingToken,
        ...(phone ? { phone: phone } : {})
      })
    });

    return response.json();
  };

  const handleProfileSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      setError("");
      try {
        setValidated(false);
        setStep("mfa-method");
      } catch (requestError) {
        console.error(requestError);
      }
    }
  };

  const handleMfaMethodSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      setError("");
      try {
        const sanitizedPhone = mfaMethod === "sms" ? phoneNumber.replace(/\D/g, "") : undefined;
        const data = await requestMfa(mfaMethod, sanitizedPhone);
        if (data.success) {
          setValidated(false);
          setStep("mfa");
        } else {
          setError(data.message || "Unable to send a verification code. Please wait and try again.");
        }
      } catch (requestError) {
        console.error(requestError);
      }
    }
  };

  const handleMfaSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      setError("");
      try {
        const response = await fetch(ventrisConfig.url + '/api/verify', {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            mfa_code: mfaCode
          })
        });
        const data = await response.json();
        if (data.success) {
          setValidated(false);
          setStep("password");
        } else {
          setError("This code is incorrect. Please check and try again.");
        }
      } catch (requestError) {
        console.error(requestError);
      }
    }
  };

  const handleSendNewCode = async () => {
    setError("");
    try {
      const sanitizedPhone = mfaMethod === "sms" ? phoneNumber.replace(/\D/g, "") : undefined;
      const data = await requestMfa(mfaMethod, sanitizedPhone);
      if (!data.success) {
        setError(data.message || "Please wait before requesting another verification code.");
      }
    } catch (requestError) {
      console.error(requestError);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false || !passwordChecks.isValid || !passwordsMatch) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false && passwordChecks.isValid && passwordsMatch) {
      setValidated(true);
      setError("");
      try {
        const response = await fetch(ventrisConfig.url + '/api/password', {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            password: password,
            mfa_code: mfaCode,
            first_name: firstName,
            last_name: lastName,
            subscription_key: subscriptionKey
          })
        });
        const data = await response.json();
        if (data.success && data.token) {
          const token = data.token;
          sessionStorage.setItem("token", token);
          const accountResponse = await fetch(ventrisConfig.url + '/api/account', {
            method: 'get',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token
            }
          });
          const accountData = await accountResponse.json();
          let currentUser: any = accountData;
          sessionStorage.setItem("portal", currentUser.role.toLowerCase());
          sessionStorage.setItem("current_user", JSON.stringify(currentUser));
          document.location.href = "/";
        }
      } catch (requestError) {
        console.error(requestError);
      }
    }
  };

  const hasError = error !== "";
  const canContinueEmail = email.trim().length > 0 && !hasError;
  const canContinueSubscription = subscriptionKey.trim().length > 0 && !hasError;
  const canContinueProfile = firstName.trim().length > 0 && lastName.trim().length > 0;
  const canContinueMfaMethod = mfaMethod === "email" ? true : phoneNumber.trim().length > 0 && smsConsentAccepted;
  const canContinueMfa = mfaCode.trim().length > 0 && !hasError;
  const canContinuePassword = passwordChecks.isValid && passwordsMatch;

  return (
    <Container fluid as="main" id="main-content" tabIndex={-1} className="login-wrapper">
      <Row className="login-row">
        <Col className="login-card">
          <div className="login-card-inner">
            <div className="login-card-header">
              <img src={logo} alt="Ventris Learning Logo" className="login-logo" />
            </div>
            <div className="login-card-divider" />
            <div className="login-card-body">
              <div className="onboarding-progress">
                <div className="onboarding-progress-bar">
                  <div
                    className={`onboarding-progress-bar-fill${step === "subscription" ? " onboarding-progress-bar-fill-step2" : step === "profile" ? " onboarding-progress-bar-fill-step3" : step === "mfa-method" ? " onboarding-progress-bar-fill-step4" : step === "mfa" ? " onboarding-progress-bar-fill-step5" : step === "password" ? " onboarding-progress-bar-fill-step6" : ""}`}
                  />
                </div>
              </div>
              <h1 className="login-title">New Account Registration</h1>
              {step === "subscription" ? (
                <>
                  <p className="login-subtitle" id="onboarding-subscription-instructions">
                    Please enter the subscription key associated with
                    <br />
                    your account.
                  </p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleSubscriptionSubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="subscriptionKey">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">SUBSCRIPTION KEY</Form.Label>
                      </div>
                      <div className="login-input-field">
                        <Form.Control
                          required
                          type="text"
                          aria-describedby={hasError ? "onboarding-subscription-instructions onboarding-subscription-error" : "onboarding-subscription-instructions"}
                          aria-invalid={hasError}
                          placeholder="XXXX - XXXX - XXXX"
                          value={subscriptionKey}
                          onChange={(event) => {
                            setSubscriptionKey(event.target.value);
                            if (hasError) {
                              setError("");
                            }
                          }}
                          className={hasError ? "login-input-error" : ""}
                        />
                        {hasError ? (
                          <span className="login-input-error-icon" aria-hidden="true">!</span>
                        ) : null}
                      </div>
                      <Form.Control.Feedback type="invalid">Please enter a subscription key.</Form.Control.Feedback>
                      {hasError ? <div role="alert" className="login-input-error-text" id="onboarding-subscription-error">{error}</div> : null}
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${canContinueSubscription ? " login-btn-primary-active" : ""}`}
                        disabled={!canContinueSubscription}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : step === "profile" ? (
                <>
                  <p className="login-subtitle">Please enter your first and last name.</p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleProfileSubmit}>
                    <Form.Group className="mb-3 ventris-form-element" controlId="firstName">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">FIRST NAME</Form.Label>
                      </div>
                      <Form.Control
                        required
                        type="text"
                        autoComplete="given-name"
                        placeholder="Enter First Name"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">Please enter a first name.</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-2 ventris-form-element" controlId="lastName">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">LAST NAME</Form.Label>
                      </div>
                      <Form.Control
                        required
                        type="text"
                        autoComplete="family-name"
                        placeholder="Enter Last Name"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">Please enter a last name.</Form.Control.Feedback>
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${canContinueProfile ? " login-btn-primary-active" : ""}`}
                        disabled={!canContinueProfile}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : step === "mfa-method" ? (
                <>
                  <p className="login-subtitle" id="onboarding-mfa-method-instructions">
                    Please select your desired method for multi-factor
                    <br />
                    account verification.
                  </p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleMfaMethodSubmit}>
                    <fieldset className="mb-2 ventris-form-element mfa-method-fieldset" aria-describedby="onboarding-mfa-method-instructions">
                      <legend className="form-label-text">SELECT METHOD</legend>
                      <div className="mfa-methods">
                        <label className={`mfa-method-option${mfaMethod === "sms" ? " mfa-method-option--active" : ""}`}>
                          <input
                            className="mfa-method-native-input"
                            type="radio"
                            name="mfa-method"
                            value="sms"
                            checked={mfaMethod === "sms"}
                            onChange={() => setMfaMethod("sms")}
                          />
                          <span className="mfa-method-check" aria-hidden="true" />
                          Mobile Phone
                        </label>
                        <label className={`mfa-method-option${mfaMethod === "email" ? " mfa-method-option--active" : ""}`}>
                          <input
                            className="mfa-method-native-input"
                            type="radio"
                            name="mfa-method"
                            value="email"
                            checked={mfaMethod === "email"}
                            onChange={() => setMfaMethod("email")}
                          />
                          <span className="mfa-method-check" aria-hidden="true" />
                          Email Address
                        </label>
                      </div>
                    </fieldset>
                    {mfaMethod === "sms" ? (
                      <>
                        <Form.Group className="mb-2 ventris-form-element" controlId="phoneNumber">
                          <div className="login-label-row">
                            <Form.Label className="form-label-text">MOBILE PHONE NUMBER</Form.Label>
                          </div>
                          <Form.Control
                            required
                            type="tel"
                            autoComplete="tel"
                            aria-describedby="onboarding-phone-format"
                            placeholder="555 - 321 - 9876"
                            value={phoneNumber}
                            onChange={(event) => setPhoneNumber(event.target.value)}
                          />
                          <Form.Text id="onboarding-phone-format">Enter a 10-digit mobile phone number.</Form.Text>
                          <Form.Control.Feedback type="invalid">Please enter a mobile phone number.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-2 ventris-form-element" controlId="smsConsent">
                          <Form.Check
                            required
                            type="checkbox"
                            label="By selecting SMS, you agree to receive one-time passcodes from Ventris Learning for account authentication. Message frequency varies (sent only when you attempt to log in or verify your account). Message and data rates may apply. Reply STOP to opt out, HELP for help."
                            checked={smsConsentAccepted}
                            onChange={(event) => setSmsConsentAccepted(event.target.checked)}
                            className="mfa-consent-check"
                          />
                          <Form.Control.Feedback type="invalid">
                            You must agree to SMS consent before enabling SMS MFA.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </>
                    ) : null}
                    <div className="mfa-legal-links">
                      <a
                        className="login-link"
                        href="https://www.ventrislearning.com/terms-of-use/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms
                      </a>
                      <span aria-hidden="true">|</span>
                      <a
                        className="login-link"
                        href="https://www.ventrislearning.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </div>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${canContinueMfaMethod ? " login-btn-primary-active" : ""}`}
                        disabled={!canContinueMfaMethod}
                      >
                        {mfaMethod === "sms" ? "Confirm/Enable SMS MFA" : "Continue"}
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : step === "mfa" ? (
                <>
                  <p className="login-subtitle" id="onboarding-code-instructions">
                    {mfaMethod === "sms"
                      ? "Please enter the code that was texted to your mobile phone."
                      : `Please enter the code that was emailed to ${maskedEmail}.`}
                    <br />
                    This code will expire in 5 minutes.
                  </p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleMfaSubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="verificationCode">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">VERIFICATION CODE</Form.Label>
                        <span className="login-label-hint">
                          Code expired?{" "}
                          <button type="button" className="login-link login-link-button" onClick={handleSendNewCode}>Send new code.</button>
                        </span>
                      </div>
                      <div className="login-input-field">
                        <Form.Control
                          required
                          type="text"
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          aria-describedby={hasError ? "onboarding-code-instructions onboarding-code-error" : "onboarding-code-instructions"}
                          aria-invalid={hasError}
                          placeholder="Enter the 6-Digit Code"
                          value={mfaCode}
                          onChange={(event) => {
                            setMfaCode(event.target.value);
                            if (hasError) {
                              setError("");
                            }
                          }}
                          className={hasError ? "login-input-error" : ""}
                        />
                        {hasError ? (
                          <span className="login-input-error-icon" aria-hidden="true">!</span>
                        ) : null}
                      </div>
                      <Form.Control.Feedback type="invalid">Please enter a code.</Form.Control.Feedback>
                      {hasError ? <div role="alert" className="login-input-error-text" id="onboarding-code-error">{error}</div> : null}
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${canContinueMfa ? " login-btn-primary-active" : ""}`}
                        disabled={!canContinueMfa}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : step === "password" ? (
                <>
                  <p className="login-subtitle" id="onboarding-password-instructions">Please choose a strong password.</p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handlePasswordSubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="newPassword">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">PASSWORD</Form.Label>
                        <span className="login-label-hint">Must meet minimum requirements below.</span>
                      </div>
                      <div className="login-password-field">
                        <Form.Control
                          required
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          aria-describedby="onboarding-password-instructions onboarding-password-requirements"
                          aria-invalid={password.length > 0 && !passwordChecks.isValid}
                          placeholder="Choose a Password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5C6.5 5 2 12 2 12s4.5 7 10 7 10-7 10-7-4.5-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                          </svg>
                        </button>
                      </div>
                      <Form.Control.Feedback type="invalid">Please enter a password.</Form.Control.Feedback>
                      <div className="login-password-requirements" id="onboarding-password-requirements">
                        <span className={`login-requirement${passwordChecks.hasMinLength ? " login-requirement--met" : ""}`}>8 Characters</span>
                        <span className={`login-requirement${passwordChecks.hasNumber ? " login-requirement--met" : ""}`}>1 Number</span>
                        <span className={`login-requirement${passwordChecks.hasSymbol ? " login-requirement--met" : ""}`}>1 Symbol</span>
                        <span className={`login-requirement${passwordChecks.hasUppercase ? " login-requirement--met" : ""}`}>1 Capital Letter</span>
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-2 ventris-form-element" controlId="confirmPassword">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">CONFIRM PASSWORD</Form.Label>
                        <span className="login-label-hint">Passwords must match.</span>
                      </div>
                      <div className="login-password-field">
                        <Form.Control
                          required
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          aria-invalid={passwordMismatch}
                          placeholder="Re-enter your Password"
                          value={confirmPassword}
                          onChange={(event) => setConfirmPassword(event.target.value)}
                          className={passwordMismatch ? "login-input-error" : ""}
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5C6.5 5 2 12 2 12s4.5 7 10 7 10-7 10-7-4.5-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                          </svg>
                        </button>
                        {passwordMismatch ? (
                          <span className="login-input-error-icon" aria-hidden="true">!</span>
                        ) : null}
                      </div>
                      <Form.Control.Feedback type="invalid">Please confirm your password.</Form.Control.Feedback>
                      {passwordMismatch ? (
                        <div role="alert" className="login-input-error-text">Passwords do not match, please check and try again.</div>
                      ) : null}
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${canContinuePassword ? " login-btn-primary-active" : ""}`}
                        disabled={!canContinuePassword}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : (
                <>
                  <p className="login-subtitle" id="onboarding-email-instructions">Enter the email address associated with your account.</p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleEmailSubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="email">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">EMAIL ADDRESS</Form.Label>
                      </div>
                      <div className="login-input-field">
                        <Form.Control
                          required
                          type="email"
                          autoComplete="email"
                          aria-describedby={hasError ? "onboarding-email-instructions onboarding-email-error" : "onboarding-email-instructions"}
                          aria-invalid={hasError}
                          placeholder="Enter Email Address"
                          value={email}
                          onChange={(event) => {
                            setEmail(event.target.value);
                            if (hasError) {
                              setError("");
                            }
                          }}
                          className={hasError ? "login-input-error" : ""}
                        />
                        {hasError ? (
                          <span className="login-input-error-icon" aria-hidden="true">!</span>
                        ) : null}
                      </div>
                      <Form.Control.Feedback type="invalid">Please enter an email.</Form.Control.Feedback>
                      {hasError ? <div role="alert" className="login-input-error-text" id="onboarding-email-error">{error}</div> : null}
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${canContinueEmail ? " login-btn-primary-active" : ""}`}
                        disabled={!canContinueEmail}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              )}
            </div>
            <div className="login-card-divider" />
            <div className="login-card-footer">
              <span className="login-help-text">
                <strong>Need help?</strong> Contact us by email at{" "}
                <a className="login-help-link" href="mailto:help@ventrislearning.com">help@ventrislearning.com</a>{" "}
                or by phone at{" "}
                <a className="login-help-link" href="tel:800-555-1234">800-555-1234</a>.
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Onboarding;
